import { compareAsc } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { FlowIndex } from '~/components/FlowIndex';
import IconButton from '~/components/buttons/NewIconButton';
import { HStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import IconClose from '~/icons/icon-close.svg?react';
import { useDocumentQuery } from '~/services/document';
import { FlowWithDocument } from '~/types/flow';
import { isDefined } from '~/types/typeGuards';
import { formatMoney } from '~/utils/money';
import { SelectedFlow, useReconcileModalContext } from './ModalContext';
import { DocumentDescriptionText, EllipsedText } from './ReconciliationCommon';
import styles from './SelectedFlowsRecap.module.scss';

const FlowIndexQuery = ({ flow }: { flow: FlowWithDocument }) => {
  const { data: document } = useDocumentQuery(flow.document.id);

  if (!document) return null;
  flow.document = document;

  return <FlowIndex flow={flow} />;
};

const LazyFlowIndex = ({ flow }: { flow: FlowWithDocument }) => {
  if (isDefined(flow.document.flows)) {
    return <FlowIndex flow={flow} />;
  } else {
    return <FlowIndexQuery flow={flow} />;
  }
};

const SelectedFlowRecap = ({ selected, onRemove }: { selected: SelectedFlow; onRemove: () => void }) => {
  const { flow, amount, split } = selected;
  const { t } = useTranslation('reconciliations');
  const document = flow.document;

  return (
    <>
      <EllipsedText variant="body2" weight="semibold">
        {document.counterpart?.companyName ?? document.counterpartName}
      </EllipsedText>

      <EllipsedText variant="body2" weight="regular" secondaryColor>
        <DocumentDescriptionText document={document} />
      </EllipsedText>

      <Text variant="body2" weight="regular">
        {split ? t('advance_amount') : <LazyFlowIndex flow={flow} />}
      </Text>

      <HStack gap={1} justify="end" align="center">
        <Text variant="body2" weight="regular">
          {formatMoney(amount)}
        </Text>
        <IconButton onClick={() => onRemove()} Icon={IconClose} variant="ghost" size={'medium'} />
      </HStack>
    </>
  );
};

export const SelectedFlowsRecap = () => {
  const { selectedFlows, removeFlow } = useReconcileModalContext();
  const orderedFlows = selectedFlows.toSorted((a, b) => {
    const doca = a.flow.document;
    const docb = b.flow.document;

    const creationDateComp = compareAsc(doca.creationDate, docb.creationDate);
    if (creationDateComp !== 0) return creationDateComp;
    if (doca.id == docb.id) {
      return compareAsc(a.flow.expectedPaymentDate, b.flow.expectedPaymentDate);
    } else {
      return doca.id.localeCompare(docb.id);
    }
  });

  return (
    <div className={styles.container}>
      {orderedFlows.map((selection) => (
        <SelectedFlowRecap
          key={selection.flow.id}
          selected={selection}
          onRemove={() => removeFlow(selection.flow.id)}
        />
      ))}
    </div>
  );
};
